import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMechanicAuth } from '../../context/MechanicAuthContext';
import {
  getMechanicNotifications,
  markNotificationRead,
  markAllNotificationsRead,
} from '../../api/mechanicAuth.api';
import '../../styles/mechanic.css';

const timeAgo = (dateStr) => {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const MechanicNotifications = () => {
  const { logoutMechanic } = useMechanicAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    getMechanicNotifications({ limit: 50 })
      .then((data) => {
        setNotifications(data.notifications || []);
        setUnreadCount(data.unreadCount || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleMarkRead = async (id) => {
    try {
      await markNotificationRead(id);
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, isRead: true } : n)));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch {
      // Ignore — the item just stays unread until the next refresh.
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch {
      // Ignore — user can retry.
    }
  };

  return (
    <div className="mechanic-page">
      <div className="mechanic-container">
        <div className="mechanic-header">
          <div>
            <h1 className="mechanic-title">Notifications</h1>
            <p className="mechanic-subtitle">{unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}</p>
          </div>
          <Link to="/mechanic/dashboard" className="mechanic-back-btn">← Dashboard</Link>
        </div>

        {unreadCount > 0 && (
          <div style={{ marginBottom: '1rem' }}>
            <button className="mechanic-action-btn primary" onClick={handleMarkAllRead}>
              Mark all as read
            </button>
          </div>
        )}

        {loading ? (
          <div className="mechanic-loading"><div className="auth-spinner" /></div>
        ) : notifications.length === 0 ? (
          <div className="mechanic-empty">
            <p>No notifications yet.</p>
          </div>
        ) : (
          <div className="mechanic-job-list">
            {notifications.map((n) => (
              <div
                key={n.id}
                className="mechanic-job-card"
                style={{
                  borderLeft: `3px solid ${n.isRead ? 'transparent' : '#2563eb'}`,
                  background: n.isRead ? undefined : '#eff6ff',
                  cursor: n.isRead ? 'default' : 'pointer',
                }}
                onClick={() => !n.isRead && handleMarkRead(n.id)}
              >
                <div className="mechanic-job-info">
                  <div className="mechanic-job-customer">{n.title}</div>
                  <div className="mechanic-job-service">{n.message}</div>
                  <div className="mechanic-job-date">{timeAgo(n.createdAt)}</div>
                </div>
                {!n.isRead && <span className="mechanic-status-badge" style={{ background: '#2563eb20', color: '#2563eb' }}>New</span>}
              </div>
            ))}
          </div>
        )}

        <div className="mechanic-quick-links" style={{ marginTop: '2rem' }}>
          <button className="mechanic-logout-btn" onClick={logoutMechanic}>Logout</button>
        </div>
      </div>
    </div>
  );
};

export default MechanicNotifications;
